import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11AdminLayout from "./P11AdminLayout";
import { useP11Role } from "./P11AdminGuard";
import { P11 } from "./p11shared";

// IDENTIFICATION MANAGEMENT — toewijzen, vrijgeven, transfer. Alleen owner kan muteren.
export default function P11AdminNumbers() {
  const role = useP11Role();
  const [data, setData] = useState(null);
  const [transfer, setTransfer] = useState(null); // assignment om te transferen
  const [toPid, setToPid] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/project11/admin/numbers").then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const release = async (a) => {
    setBusy(true);
    try {
      await api.post("/project11/admin/numbers/release", { participant_id: a.participant_id });
      toast.success(`${a.number_display} vrijgegeven`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); } finally { setBusy(false); }
  };

  const doTransfer = async () => {
    setBusy(true);
    try {
      await api.post("/project11/admin/numbers/transfer", { from_participant_id: transfer.participant_id, to_participant_id: toPid });
      toast.success("Nummer overgedragen (alleen het nummer — geen stempels of gegevens)");
      setTransfer(null); setToPid("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Transfer mislukt"); } finally { setBusy(false); }
  };

  if (!data) return <P11AdminLayout><span className="p11-label">…</span></P11AdminLayout>;

  return (
    <P11AdminLayout>
      <div data-testid="p11-admin-numbers">
        <div className="p11-label-yellow">IDENTIFICATION MANAGEMENT</div>
        <h1 className="p11-h1 text-3xl mt-4 mb-2">#001 — #{String(data.capacity).padStart(3, "0")}.</h1>
        <p className="p11-label mb-8">{data.available.length} BESCHIKBAAR · BEREIK VOLGT PROJECTCAPACITEIT</p>

        <div className="p11-label mb-4">// HUIDIGE TOEWIJZINGEN</div>
        <div className="divide-y mb-12" style={{ borderColor: P11.border }} data-testid="p11-numbers-current">
          {data.current.length === 0 && <div className="p11-label py-4" style={{ color: P11.gray }}>Nog geen toewijzingen.</div>}
          {data.current.map((a) => (
            <div key={a.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`p11-number-row-${a.number}`}>
              <span className="p11-mono text-xl w-20" style={{ color: P11.yellow }}>{a.number_display}</span>
              <span className="flex-1 min-w-40 text-sm" style={{ color: P11.text }}>{a.name}</span>
              <span className="p11-label" style={{ color: P11.gray }}>{a.assigned_at?.slice(0, 10)}</span>
              {role === "owner" && (
                <div className="flex gap-2">
                  <button onClick={() => setTransfer(a)} disabled={busy} className="p11-label px-3 py-1.5 border" style={{ borderColor: P11.border, color: P11.gray2 }} data-testid={`p11-number-transfer-${a.number}`}>TRANSFER</button>
                  <button onClick={() => release(a)} disabled={busy} className="p11-label px-3 py-1.5 border" style={{ borderColor: "#7a2d2d", color: "#e08585" }} data-testid={`p11-number-release-${a.number}`}>RELEASE</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p11-label mb-4">// TOEWIJZINGSGESCHIEDENIS</div>
        <div className="divide-y" style={{ borderColor: P11.border }} data-testid="p11-numbers-history">
          {data.history.length === 0 && <div className="p11-label py-4" style={{ color: P11.gray }}>Geen historie.</div>}
          {data.history.map((h) => (
            <div key={h.id} className="py-2.5 p11-label flex gap-4" style={{ color: P11.gray2 }} data-testid={`p11-number-history-${h.id}`}>
              <span style={{ color: P11.text }}>{h.number_display}</span>
              <span>{h.assigned_at?.slice(0, 10)} → {h.released_at?.slice(0, 10)}</span>
              <span>{h.released_by}</span>
            </div>
          ))}
        </div>

        {transfer && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setTransfer(null)}>
            <div className="bg-[#080808] border max-w-sm w-full p-8" style={{ borderColor: P11.border }} onClick={(e) => e.stopPropagation()} data-testid="p11-transfer-modal">
              <div className="p11-label-yellow mb-4">// TRANSFER {transfer.number_display}</div>
              <p className="text-sm mb-4" style={{ color: P11.gray2 }}>Alleen het zichtbare nummer gaat over. Aanwezigheid, stempels, account en medische gegevens blijven bij de oorspronkelijke deelnemer.</p>
              <label className="p11-label">Naar deelnemer (participant ID)</label>
              <input value={toPid} onChange={(e) => setToPid(e.target.value)} className="p11-input mt-1" placeholder="uuid van bevestigde deelnemer" data-testid="p11-transfer-to" />
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setTransfer(null)} className="p11-btn-ghost" data-testid="p11-transfer-no">ANNULEREN</button>
                <button onClick={doTransfer} disabled={busy || !toPid.trim()} className="p11-btn" data-testid="p11-transfer-yes">{busy ? "…" : "TRANSFER"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </P11AdminLayout>
  );
}
